import { Box, Grid2 } from '@mui/material';
import React, { useEffect, useState } from 'react';
import ExpandSvg from '@assets/images/expand.svg';

import FacilityLogo from '@assets/images/facility_logo.svg';
import CustomCardContent from '@ui-components/Card/CustomCardContent';
import CustomCard from '@ui-components/Card/CustomCard';
import CustomDivider from '@ui-components/Divider/CustomDivider';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import DoughnutChart from './Charts';
import LineChart from './LineChart';
import CustomDialog from '@ui-components/Dialog/CustomDialog';
import CustomDialogContent from '@ui-components/Dialog/CustomDialogContent';
import CustomToggleButton from '@ui-components/ToggleButton/CustomToggleButton';

// Icons
import LineGraphIcon from '@assets/images/line_graph.svg';
import PieChartIcon from '@assets/images/pie_chart.svg';
import type {
  MetricCardData,
  MetricsResponse,
} from '../../API/services/metricsService';
import { CustomIconButton } from '@qler-view/shared_lib/src/index.js';
import Close from '@assets/images/Close.svg';

interface MetricsChartProps {
  metricsChartData: MetricsResponse | null;
  selectedFont: string;
  filteredMetricsData: MetricCardData[] | undefined | [];
}

const MetricCard: React.FC<MetricsChartProps> = ({
  metricsChartData,
  selectedFont,
  filteredMetricsData,
}) => {
  const [clickedId, setClickedId] = useState<number | null>(null);
  const [clickedIds, setClickedIds] = useState<number[] | null>(null);
  const [expandGraph, setExpandGraph] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState<MetricCardData | null>();
  const [sortedData, setSortedData] = useState<MetricCardData[] | null>(null);
  const defaultOptions = [
    { value: 1, label: '1 W' },
    { value: 2, label: '1 M' },
    { value: 3, label: '6 M' },
    { value: 4, label: '1 Y' },
    { value: 5, label: '5 Y' },
  ];

  useEffect(() => {
    if (filteredMetricsData && filteredMetricsData.length > 0) {
      setSortedData(filteredMetricsData);
    } else if (
      metricsChartData &&
      (selectedFont === 'atoz' ||
        selectedFont === 'ztoa' ||
        selectedFont === 'reqdesc' ||
        selectedFont === 'reqasc')
    ) {
      const sortedData = [...metricsChartData.metricCardData].sort((a, b) => {
        if (a?.facilityName && b?.facilityName) {
          if (selectedFont === 'atoz' || selectedFont === 'ztoa') {
            const comparison = a.facilityName.localeCompare(b.facilityName);
            return selectedFont === 'atoz' ? comparison : -comparison;
          } else if (selectedFont === 'reqasc' || selectedFont === 'reqdesc') {
            const comparison = a.totalRequest - b.totalRequest;
            return selectedFont === 'reqdesc' ? -comparison : comparison;
          }
        }
        return 0;
      });

      setSortedData(sortedData);
    }
  }, [metricsChartData, selectedFont, filteredMetricsData]);

  const [selectedddlValue, setSelectedddlValue] = useState<string | number>(
    defaultOptions[0].value
  );
  const handleExpandClick = (metricData: MetricCardData) => {
    setSelectedMetric(metricData);
    setClickedId(metricData.facilityId);
    setExpandGraph(true);
    setSelectedddlValue(1);
    setSelected([defaultOptions[0].value.toString()]);
  };
  const handleGraphClose = () => {
    setExpandGraph(false);
    setSelectedMetric(null);
  };

  const handleSelectChange = (value: string | number) => {
    setSelectedddlValue(value as number);
  };

  const [selected, setSelected] = useState<string[]>([]);

  const handleToggleClickedId = (metricData: MetricCardData) => {
    setClickedIds((prevState) => {
      if (!prevState) {
        return [metricData.facilityId];
      } else if (prevState.includes(metricData.facilityId)) {
        return prevState.filter((id) => id !== metricData.facilityId);
      }

      return [...prevState, metricData.facilityId];
    });
  };

  const handleToggleClickedIdState = (metricData: MetricCardData) => {
    setClickedId((prevId) =>
      prevId === metricData.facilityId ? null : metricData.facilityId
    );
  };

  return (
    <Box className="pb-24">
      <Grid2 container alignItems="stretch" spacing={3}>
        {sortedData?.map((metricData) => (
          <Grid2
            key={metricData.facilityId}
            size={{ xs: 12, sm: 12, md: 6, lg: 4 }}
          >
            <CustomCard id="patient-details" height="100%">
              <CustomCardContent
                id="patient-details-content"
                className="metric-card-container"
              >
                <Box className="flex align-items-center">
                  <Grid2 size={{ xs: 7, md: 7, lg: 7.5, xl: 7.5 }}>
                    <Box display="flex" alignItems="center">
                      <Box className="mr-12">
                        <ExpandSvg />
                      </Box>
                      <Box className="text-align-left">
                        <CustomTypography id="patient-name" variant="h5">
                          {metricData.facilityName
                            ? metricData.facilityName
                            : 'N/A'}
                        </CustomTypography>
                        <Box className="flex">
                          <CustomTypography
                            id="patient-name"
                            variant="body2"
                            color="textSecondary"
                          >
                            Total Request:&nbsp;
                          </CustomTypography>
                          <CustomTypography
                            id="patient-name"
                            variant="body2"
                            color="textPrimary"
                          >
                            {metricData.totalRequest
                              ? metricData.totalRequest
                              : 0}
                          </CustomTypography>
                        </Box>
                      </Box>
                    </Box>
                  </Grid2>
                  <Grid2
                    size={{ xs: 5.5, md: 5.5, lg: 5, xl: 5 }}
                    className="flex align-items-center justify-end"
                    justifyContent={'flex-end'}
                  >
                    <Grid2 container spacing={2} className="cursor-pointer">
                      <Grid2>
                        <Box
                          onClick={() => {
                            handleToggleClickedId(metricData);
                            handleToggleClickedIdState(metricData);
                          }}
                        >
                          {clickedIds?.includes(metricData.facilityId) ? (
                            <PieChartIcon />
                          ) : (
                            <LineGraphIcon />
                          )}
                        </Box>
                      </Grid2>
                      {clickedIds?.includes(metricData.facilityId) && (
                        <Grid2>
                          <Box onClick={() => handleExpandClick(metricData)}>
                            <FacilityLogo />
                          </Box>
                        </Grid2>
                      )}
                    </Grid2>
                  </Grid2>
                </Box>
                <Box className="pt-12">
                  <CustomDivider />
                </Box>
                <Grid2 container>
                  <Grid2 size={{ xs: 12 }}>
                    <Box>
                      {clickedIds?.includes(metricData.facilityId) &&
                      metricData.lineChart !== null ? (
                        <Box>
                          <LineChart
                            selectedPeriod={1}
                            lineData={metricData.lineChart}
                            Manualheight={170}
                          />
                        </Box>
                      ) : (
                        <Box>
                          <DoughnutChart donutData={metricData} />
                        </Box>
                      )}
                    </Box>
                  </Grid2>
                </Grid2>
              </CustomCardContent>
            </CustomCard>
          </Grid2>
        ))}
      </Grid2>

      {expandGraph === true && (
        <Box className="pr-12">
          <Box className="pr-12">
            <CustomDialog
              id="dialogWarning"
              open={expandGraph}
              onClose={handleGraphClose}
              disableEscapeKeyDown={true}
              maxWidth="md"
              fullWidth
            >
              <CustomDialogContent className="consult-card-container">
                <Box>
                  {selectedMetric && (
                    <>
                      <Box>
                        <Grid2 container alignItems="center">
                          <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6 }}>
                            <Box display="flex" alignItems="center">
                              <Box className="pb-8 pr-12">
                                <ExpandSvg />
                              </Box>
                              <Box>
                                <CustomTypography
                                  id="patient-name"
                                  variant="h5"
                                  className="whitespace"
                                >
                                  {selectedMetric.facilityName}
                                </CustomTypography>
                                <CustomTypography
                                  id="patient-name"
                                  variant="body3"
                                  color="textSecondary"
                                  className="pb-8"
                                >
                                  Total Request:&nbsp;
                                  {selectedMetric.totalRequest}
                                </CustomTypography>
                              </Box>
                            </Box>
                          </Grid2>
                          <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6 }}>
                            <Box className="flex justify-end pb-8">
                              <CustomIconButton
                                id="close-button"
                                icon={<Close />}
                                onClick={handleGraphClose}
                              ></CustomIconButton>
                            </Box>
                            <Box display="flex" justifyContent="flex-end">
                              <CustomToggleButton
                                id="history-toggle-button"
                                options={defaultOptions}
                                value={selected}
                                onChange={(newValue) =>
                                  setSelected([
                                    ...(Array.isArray(newValue)
                                      ? newValue
                                      : [newValue]),
                                  ])
                                }
                                onSelectChange={(newValue) =>
                                  handleSelectChange(newValue as string)
                                }
                                multiple
                              />
                            </Box>
                          </Grid2>
                        </Grid2>
                      </Box>

                      <Box className="cursor-pointer">
                        {selectedMetric.facilityId === clickedId &&
                        selectedMetric.lineChart !== null ? (
                          <LineChart
                            selectedPeriod={selectedddlValue}
                            lineData={selectedMetric.lineChart}
                            Manualheight={320}
                          />
                        ) : (
                          <DoughnutChart donutData={selectedMetric} />
                        )}
                      </Box>
                    </>
                  )}
                </Box>
              </CustomDialogContent>
            </CustomDialog>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default MetricCard;
